import axios from "axios";

import { API_DOMAIN_REGEX, HALLIDAY_PAYMENTS_API_URL } from "@shared/constants";
import { HSON } from "@halliday-lib/hson";
import {
    type ResponseValidationOptions,
    defaultValidationOptions,
    createResponseValidationInterceptor,
} from "./responseValidationInterceptor";
import { createErrorInterceptor, PaymentsApiClientError, ErrorCodes } from "./errorInterceptor";

export { PaymentsApiClientError, defaultValidationOptions, ErrorCodes, type ResponseValidationOptions };

/**
 * Creates a new axios instance with the given options.
 * @param {Object} options The options for the axios instance.
 * @param {!string} options.apiKey {string} The API key for the axios instance.
 * @param {string=} options.baseUrl {string} The base URL for the axios instance.
 * @param {ResponseValidationOptions=} options.responseValidationOptions {@link ResponseValidationOptions} The options for the response validation interceptor.
 * @returns {AxiosInstance} The axios instance.
 */
const createAxiosApiClient = ({
    apiKey,
    baseUrl = HALLIDAY_PAYMENTS_API_URL,
    responseValidationOptions = defaultValidationOptions,
}: {
    apiKey: string;
    baseUrl?: string;
    responseValidationOptions?: ResponseValidationOptions;
}) => {
    if (!API_DOMAIN_REGEX.test(baseUrl)) {
        throw new Error(`Invalid base URL: ${baseUrl}`);
    }
    if (!apiKey) {
        throw new Error("API key is required");
    }

    const instance = axios.create({
        baseURL: baseUrl,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
        },
        parseReviver: HSON.reviver,
    });

    // Add response validation interceptor
    const validationInterceptor = createResponseValidationInterceptor(responseValidationOptions);
    instance.interceptors.response.use(validationInterceptor.onFulfilled, validationInterceptor.onRejected);

    // Add error interceptor
    const errorInterceptor = createErrorInterceptor();
    instance.interceptors.response.use(errorInterceptor.onFulfilled, errorInterceptor.onRejected);

    return instance;
};

export default createAxiosApiClient;
